import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { useActivityLogs } from "@/components/pages/dashboard/hooks/useActivityLogs";
import {
  formatReservationLogMessage,
  formatTime,
} from "@/utils/formatActivityLogDisplay";

interface ReservationLogsBookingInfoProps {
  bookingId?: string;
}

const RESERVATION_LOG_SERVICES = ["BookingService", "BookingCheckinService"];

export const ReservationLogsBookingInfo: FC<
  ReservationLogsBookingInfoProps
> = ({ bookingId }) => {
  const { t } = useTranslation();

  const filters = useMemo(
    () =>
      bookingId
        ? {
            bookingId,
            service: RESERVATION_LOG_SERVICES,
            limit: 100,
            sort: "desc" as const,
          }
        : undefined,
    [bookingId],
  );

  const { logs: reservationLogs, isLoading } = useActivityLogs(filters);

  if (!bookingId || (!isLoading && reservationLogs.length === 0)) {
    return null;
  }

  const extraNode = reservationLogs.length ? (
    <Badge variant="outline">{reservationLogs.length}</Badge>
  ) : undefined;

  return (
    <FoldableContainer
      titleNode={t("chats.reservationLogs")}
      extraNode={extraNode}
    >
      {isLoading && reservationLogs.length === 0 ? (
        <p className="text-sm text-neutral-500">
          {t("reservation.otherSection.logsLoading")}
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {reservationLogs.map((entry) => (
            <div
              key={entry.id}
              className="flex items-start justify-between gap-3 text-sm"
            >
              <span className="shrink-0 text-neutral-400">
                {formatTime(entry.createdAt)}
              </span>
              <span className="text-eathy-comfort-1 text-right">
                {formatReservationLogMessage(entry)}
              </span>
            </div>
          ))}
        </div>
      )}
    </FoldableContainer>
  );
};
